import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import ideaStatusGroupsQuery from '@aurora/shared-client/components/ideas/IdeaStatusGroups.query.graphql';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import { dropdownPopperConfig } from '@aurora/shared-client/helpers/ui/PopperJsHelper';
import type { Idea, IdeaStatusDetails } from '@aurora/shared-generated/types/graphql-schema-types';
import type {
  IdeaStatusGroupsQuery,
  IdeaStatusGroupsQueryVariables
} from '@aurora/shared-generated/types/graphql-types';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import React, { forwardRef } from 'react';
import type {
  ForwardRefExoticComponent,
  MouseEventHandler,
  PropsWithoutRef,
  RefAttributes
} from 'react';
import { Dropdown, useClassNameMapper } from 'react-bootstrap';
import useTranslation from './../../../useTranslation';
import localStyles from './AddFeaturedIdeaStatusesModal.module.pcss';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import IconTypes from '@aurora/shared-client/public/static/graphics/processed/enums';
import { IconColor, IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import { extractStatusesFromGroupTree } from '@aurora/shared-client/helpers/ideas/IdeasHelper';

interface Props {
  /**
   * The node which filtering should be scoped to.
   */
  contextNodeId: string;
  /**
   * Statuses to exclude from the dropdown
   */
  excludeStatusKeys: string[];
  /**
   * Class name(s) to apply to the component element.
   */
  className?: string;
  /**
   * Callback function when a status filter is selected.
   *
   * @callback
   */
  onChange?: (item: IdeaStatusDetails) => void;
}

interface CustomToggleProps {
  /**
   * Button onClick handler
   * @param event - the DOM event
   */
  onClick?: MouseEventHandler;
}

/**
 * The idea statuses dropdown for the add featured idea statuses modal
 *
 * @author rishabh.jivan
 */
const AddFeaturedIdeaStatusesDropdown = ({
  className,
  contextNodeId,
  excludeStatusKeys,
  onChange
}: Props): React.ReactElement => {
  const i18n = useTranslation(EndUserComponent.ADD_FEATURED_IDEA_STATUSES_MODAL);
  const { formatMessage } = i18n;
  const cx = useClassNameMapper(localStyles);

  /**
   * A custom toggle component used in the dropdown below
   */
  const CustomToggle: ForwardRefExoticComponent<
    PropsWithoutRef<CustomToggleProps> & RefAttributes<HTMLButtonElement>
  > = forwardRef(function CustomToggleForward({ onClick }, ref) {
    return (
      <Button
        variant={ButtonVariant.UNSTYLED}
        ref={ref}
        onClick={event => {
          event.preventDefault();
          onClick(event);
        }}
        className={cx('lia-custom-toggle')}
        data-testid="IdeaStatuses.Dropdown.CustomToggle"
      >
        <Icon
          icon={IconTypes.AddSolidIcon}
          size={IconSize.PX_16}
          color={IconColor.GRAY_900}
          className={cx('lia-icon')}
        />
        <span>{formatMessage('addStatus')}</span>
      </Button>
    );
  });

  const { data: ideaGroupsResult, loading: statusLoading } = useQueryWithTracing<
    IdeaStatusGroupsQuery,
    IdeaStatusGroupsQueryVariables
  >(module, ideaStatusGroupsQuery, {
    variables: {
      id: contextNodeId,
      useGroupKey: true,
      useDescription: false,
      useLabel: true
    }
  });

  /**
   * Renders individual idea status items in the dropdown
   * @param item
   */
  function renderStatusDropdownMenuItem(item: IdeaStatusDetails): React.ReactElement {
    return (
      <Dropdown.Item
        key={item.statusKey}
        onSelect={(): void => onChange(item)}
        data-testid={`IdeaStatuses.Dropdown.Item.${item.statusKey}`}
      >
        <span aria-label={item.text}>{item.text}</span>
      </Dropdown.Item>
    );
  }

  if (statusLoading || i18n.loading) {
    return null;
  }

  const ideaStatusList: IdeaStatusDetails[] = extractStatusesFromGroupTree(
    (ideaGroupsResult.coreNode as Idea).ideaStatusGroups.groups,
    excludeStatusKeys,
    'excludes'
  ).filter(status => status.label.visible);

  if (ideaStatusList.length === 0) {
    return null;
  }

  return (
    <Dropdown className={cx(className)} data-testid="IdeaStatuses.Dropdown">
      <Dropdown.Toggle
        as={CustomToggle}
        variant={ButtonVariant.UNSTYLED}
        className={cx('lia-g-dropdown-toggle lia-sort-dropdown')}
        aria-label={formatMessage('addStatus')}
      />
      <Dropdown.Menu
        align="left"
        popperConfig={dropdownPopperConfig}
        className={cx('lia-w-100')}
        data-testid="IdeaStatuses.Dropdown.Menu"
      >
        {ideaStatusList.map(ideaStatus => renderStatusDropdownMenuItem(ideaStatus))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default AddFeaturedIdeaStatusesDropdown;
